/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ComponentRef} from '@angular/core';

import {OutletContext} from './router_outlet_context';
import {ActivatedRoute, ActivatedRouteSnapshot} from './router_state';
import {TreeNode} from './utils/tree';

/**
 * @whatItDoes Represents the detached route tree.
 *
 * This is an opaque value the router will give to a custom route reuse strategy
 * to store and retrieve later on.
 *
 * @experimental
 */
export type DetachedRouteHandle = {};

/** @internal */
export type DetachedRouteHandleInternal = {
  contexts: Map<string, OutletContext>,
  componentRef: ComponentRef<any>,
  route: TreeNode<ActivatedRoute>,
};
/**
 * \@whatItDoes Provides a way to customize when activated routes get reused.
 * 
 * \@experimental
 * @abstract
 */
export abstract class RouteReuseStrategy {
/**
 * Determines if this route (and its subtree) should be detached to be reused later
 * @abstract
 * @param {?} route
 * @return {?}
 */
shouldDetach(route: ActivatedRouteSnapshot) {}
/**
 * Stores the detached route.
 * 
 * Storing a `null` value should erase the previously stored value.
 * @abstract
 * @param {?} route
 * @param {?} handle
 * @return {?}
 */
store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle|null) {}
/**
 * Determines if this route (and its subtree) should be reattached
 * @abstract
 * @param {?} route
 * @return {?}
 */
shouldAttach(route: ActivatedRouteSnapshot) {}
/**
 * Retrieves the previously stored route
 * @abstract
 * @param {?} route
 * @return {?}
 */
retrieve(route: ActivatedRouteSnapshot) {}
/**
 * Determines if a route should be reused
 * @abstract
 * @param {?} future
 * @param {?} curr
 * @return {?}
 */
shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) {}
}
/**
 * Does not detach any subtrees. Reuses routes as long as their route config is the same.
 */
export class DefaultRouteReuseStrategy implements RouteReuseStrategy {
/**
 * @param {?} route
 * @return {?}
 */
shouldDetach(route: ActivatedRouteSnapshot): boolean { return false; }
/**
 * @param {?} route
 * @param {?} detachedTree
 * @return {?}
 */
store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle): void {}
/**
 * @param {?} route
 * @return {?}
 */
shouldAttach(route: ActivatedRouteSnapshot): boolean { return false; }
/**
 * @param {?} route
 * @return {?}
 */
retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle|null { return null; }
/**
 * @param {?} future
 * @param {?} curr
 * @return {?}
 */
shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
